import React, { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { ConfirmedStepsData, ENROLL_REQUEST_STATE } from '../../../lib/models/enroll';
import { RootState } from '../../../lib/store';
import { autoLogin } from '../../../lib/store/common-actions';

import EmailSentDone from '../../../assets/img/email-sent-ace-done.png';
import IconClose from '../../../assets/img/icon-close.svg';
import { GTM_CATEGORIES, GTM_CLIENT_CREATION_STEPS, GTM_EVENTS } from '../../../constants/gtm';
import Button from '../../../shared_components/Button/Button';
import { hashSHA256 } from '../../../util/hash';
import { BaseModal, ClickableImage } from '../../index';

import styles from './EmailConfirmedModal.module.scss';

type Props = {
  forceNoRender?: boolean;
  noModalDisplay?: boolean;
  isOpened: boolean;
  confirmedSteps?: ConfirmedStepsData;
  authStatus?: ENROLL_REQUEST_STATE | undefined;
  userId?: number;
};

const EmailConfirmedModal = ({
  isOpened,
  forceNoRender,
  noModalDisplay,
  confirmedSteps,
  authStatus,
  userId,
}: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState<boolean>(isOpened);

  const [redirectToLogin, setRedirectToLogin] = useState<boolean>(false);

  // Push GTM event for email verified
  useEffect(() => {
    const pushGtmEvent = async () => {
      const email = confirmedSteps?.email;
      if (email && typeof email === 'string') {
        const hashedEmail = await hashSHA256(email);
        TagManager.dataLayer({
          dataLayer: {
            event: GTM_EVENTS.CLIENT_CREATION,
            event_step: GTM_CLIENT_CREATION_STEPS.STEP_4.step,
            event_category: GTM_CATEGORIES.CLIENT_CREATION_FUNNEL,
            event_action: GTM_CLIENT_CREATION_STEPS.STEP_4.action,
            event_label: GTM_CLIENT_CREATION_STEPS.STEP_4.label,
            client_id: userId,
            user_email_hashed: hashedEmail,
          },
        });
      }
    };
    pushGtmEvent();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const descriptions = lang
    .commonOnboardingEmailConfirmedDescription1()
    .split(/\r\n|\r|\n/g)
    .map((description: string) => (
      <p
        key={description}
        className={classNames(styles.text, { [styles.lightText]: noModalDisplay })}
      >
        {description}
      </p>
    ));

  useEffect(() => {
    if (redirectToLogin && authStatus === ENROLL_REQUEST_STATE.READY) {
      dispatch(autoLogin());
    }
  }, [dispatch, redirectToLogin, authStatus]);

  const closeModal = () => {
    navigate('/');
    setShowModal(!showModal);
  };

  // Here we send information about the confirmed email to the previous tab ( VerifyEmailModal )
  try {
    const channelEmailConfirmedModalPage = new BroadcastChannel('confirmedEmail');
    channelEmailConfirmedModalPage.postMessage(confirmedSteps?.email);
  } catch {
    console.error('BroadcastChannel is not supported by the browser');
  }

  if (forceNoRender) {
    return <></>;
  }

  const content: JSX.Element = (
    <div className={styles.wrapperContent}>
      {!noModalDisplay && (
        <div className={styles.title}>{lang.commonOnboardingEmailConfirmedTitle()}</div>
      )}
      {!noModalDisplay && (
        <ClickableImage
          alt=""
          src={IconClose}
          aid="closeModalImg"
          aria-label="close email confirmed modal"
          className={styles.closeIcon}
          onClick={closeModal}
        />
      )}

      <img src={EmailSentDone} alt="email confirmed" className={styles.image} />
      {noModalDisplay && (
        <div className={classNames(styles.text, { [styles.lightText]: noModalDisplay })}>
          <p className={styles.title}>{lang.commonOnboardingEmailConfirmedTitle()}</p>
        </div>
      )}
      <div className={styles.textContinue}>{lang.commonOnboardingEmailCompleted()}</div>
      {!noModalDisplay && (
        <Button
          id="continue"
          fullWidth
          ariaLabel="continue"
          className={styles.button}
          onClick={() => {
            setRedirectToLogin(true);
          }}
        >
          {lang.commonContinueButton()}
        </Button>
      )}
    </div>
  );

  if (noModalDisplay) {
    return content;
  }

  return <BaseModal isOpen={showModal}>{content}</BaseModal>;
};

const mapStateToProps = (state: RootState, ownProps: Props): any => ({
  confirmedSteps: state.auth.enroll.confirmedSteps,
  authStatus: state.auth.enroll.status,
  userId: state.auth.enroll.userId,
});

export default connect(mapStateToProps)(EmailConfirmedModal);
